import { FileText, Download, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const documentos = [
  {
    id: "resumen",
    titulo: "Resumen Ejecutivo",
    descripcion:
      "Síntesis del estudio de viabilidad con las principales conclusiones y recomendaciones.",
    archivo: "/assets/docs/Resumen_Ejecutivo_SOSTRE_BALEAR.pdf",
    tamano: "711 KB",
    tipo: "PDF",
  },
  {
    id: "presentacion",
    titulo: "Presentación del Estudio",
    descripcion:
      "Presentación visual con los puntos clave del modelo SOSTRE BALEAR y su hoja de ruta.",
    archivo: "/assets/docs/Presentacion_SOSTRE_BALEAR.pdf",
    tamano: "3.3 MB",
    tipo: "PDF",
  },
];

export default function Documentacion() {
  return (
    <div className="min-h-screen pt-20" data-testid="documentacion-page">
      <Helmet>
        <title>Documentación Descargable | SOSTRE BALEAR</title>
        <meta name="description" content="Descarga la presentación y el resumen ejecutivo del estudio de viabilidad SOSTRE BALEAR para vivienda asequible en las Illes Balears." />
        <link rel="canonical" href="https://www.sostrebalear.com/documentacion" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Documentación | SOSTRE BALEAR" />
        <meta property="og:description" content="Documentos descargables del estudio SOSTRE BALEAR." />
        <meta property="og:url" content="https://www.sostrebalear.com/documentacion" />
        <meta property="og:image" content="https://www.sostrebalear.com/assets/logos/sostre-balear.png" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="SOSTRE BALEAR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Documentación | SOSTRE BALEAR" />
        <meta name="twitter:description" content="Documentos descargables del estudio SOSTRE BALEAR." />
      </Helmet>
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-wide uppercase text-[#22c55e]">
              Descargas
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 tracking-tight">
              Documentación
            </h1>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">
              Descarga los documentos principales del estudio de viabilidad
              SOSTRE BALEAR.
            </p>

            {/* Aviso de Propiedad Intelectual */}
            <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-xl">
              <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wide mb-3">
                Aviso de Propiedad Intelectual
              </h3>
              <p className="text-amber-900 text-sm leading-relaxed mb-3">
                La presente documentación ha sido elaborada por PASTORIA PROJECT SL, 
                quien ostenta en exclusiva la titularidad de todos los derechos de 
                propiedad intelectual sobre la misma.
              </p>
              <p className="text-amber-900 text-sm leading-relaxed mb-3">
                Queda expresamente prohibida su reproducción, distribución, transformación, 
                comunicación pública, comercialización o utilización para fines distintos 
                de los meramente informativos, sin la autorización previa y por escrito 
                de PASTORIA PROJECT SL.
              </p>
              <p className="text-amber-900 text-sm leading-relaxed">
                El acceso o descarga de este contenido no implica en ningún caso cesión, 
                transmisión ni licencia de derechos de propiedad intelectual.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Documents Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {documentos.map((doc) => (
              <article
                key={doc.id}
                className="bg-white rounded-2xl border border-slate-100 p-8 hover:shadow-lg transition-shadow group"
                data-testid={`doc-card-${doc.id}`}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                    <FileText className="text-red-600" size={32} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                      {doc.titulo}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">
                      {doc.descripcion}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mb-6">
                      <span className="bg-slate-100 px-2 py-1 rounded">
                        {doc.tipo}
                      </span>
                      <span>{doc.tamano}</span>
                    </div>
                    <a
                      href={doc.archivo}
                      download
                      className="inline-flex items-center gap-2 bg-[#22c55e] hover:bg-[#16a34a] text-white px-6 py-3 rounded-full font-medium text-sm transition-all"
                      data-testid={`download-${doc.id}`}
                    >
                      <Download size={18} />
                      Descargar
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">
            Recursos Adicionales
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/entregables"
              className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-[#22c55e]/30 hover:shadow-md transition-all group"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#22c55e] transition-colors">
                Entregables WP1-WP8
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Explora los 8 work packages del estudio con conclusiones
                detalladas.
              </p>
              <span className="inline-flex items-center gap-1 text-[#22c55e] text-sm font-medium">
                Ver entregables
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </span>
            </Link>

            <Link
              to="/normativa"
              className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-[#22c55e]/30 hover:shadow-md transition-all group"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#22c55e] transition-colors">
                Normativa Aplicable
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Consulta el marco legal europeo, estatal y autonómico.
              </p>
              <span className="inline-flex items-center gap-1 text-[#22c55e] text-sm font-medium">
                Ver normativa
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </span>
            </Link>

            <Link
              to="/mapa"
              className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-[#22c55e]/30 hover:shadow-md transition-all group"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#22c55e] transition-colors">
                Mapa de Suelo
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Explora las parcelas de suelo público disponibles en Baleares.
              </p>
              <span className="inline-flex items-center gap-1 text-[#22c55e] text-sm font-medium">
                Ver mapa
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Attribution */}
      <section className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600 text-sm">
            Estudio redactado por{" "}
            <span className="font-semibold text-[#22c55e]">
              PASTORIA PROJECT SL
            </span>
          </p>
          <p className="text-slate-500 text-xs mt-2">
            Los documentos están sujetos a derechos de autor. Uso permitido con
            fines informativos y de referencia.
          </p>
        </div>
      </section>
    </div>
  );
}
